import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerUser } from '../api/users';
import PasswordInput from '../components/PasswordInput';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import BookPage from '../components/common/BookPage';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setEmailError('');

    try {
      await registerUser(formData);
      navigate('/email-verification');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'email') {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
    }
  };

  return (
    <BookPage
      eyebrow="Account"
      title="Join the conversation."
      intro="Create an account to keep up with new writing and leave your own mark in the margins."
      bodyClassName="mx-auto max-w-md space-y-8"
    >
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-[color:var(--wf-brass)]">Register</p>
        <h2 className="mt-3 font-serif text-3xl text-[color:var(--wf-ink)]">Create your account.</h2>
        <p className="mt-2 text-sm leading-7 text-[color:var(--wf-ink-soft)]">
          A little less noise. A little more signal. That's the idea.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-4">
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleEmailBlur}
            placeholder="Email address"
            required
            error={emailError}
          />

          <Input
            id="first_name"
            name="first_name"
            type="text"
            value={formData.first_name}
            onChange={handleChange}
            placeholder="First name"
            required
          />

          <Input
            id="last_name"
            name="last_name"
            type="text"
            value={formData.last_name}
            onChange={handleChange}
            placeholder="Last name"
            required
          />

          <PasswordInput
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Create a password"
            required
          />
        </div>

        {error && (
          <div className="rounded-[0.8rem] border border-red-200 bg-red-50/80 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <Button type="submit" disabled={isSubmitting || !!emailError} fullWidth size="large">
          {isSubmitting ? 'Creating Account...' : 'Create Account'}
        </Button>
      </form>

      <div className="border-t border-[color:var(--wf-border)] pt-6 text-center text-sm text-[color:var(--wf-ink-soft)]">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-[color:var(--wf-plum)] transition-colors duration-200 hover:text-[color:var(--wf-ink)]">
          Sign in here
        </Link>
      </div>
    </BookPage>
  );
};

export default RegisterPage;
